import React, {useEffect, useMemo, useState} from 'react';
import {useIntl, FormattedMessage} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {Form, Formik} from 'formik';
import {Button, IconButton} from '@mui/material';
import {SVGS} from '../../assets/images';
import BaseForm from '../../components/shared/form-controls/base-form';
import {Lx} from '../../i18n/consts';
import {NAVIGATION_ROUTES} from '../../routes/NavigationRoute';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import {FavoriteFilter, FavoriteFilterPayload, FavoriteFilterScope} from '../../features/favorite-filters/types';
import {createFavoriteFilter, deleteFavoriteFilter, getFavoriteFilters} from '../../features/favorite-filters/slice';
import {
    updateTimeTrackingPropertyFilter,
    updateTimeTrackingDetailedByDay,
    updateTimeTrackingVisibleDays,
} from '../../features/time-tracking-filters';
import {VisibleDays} from '../../features/report/types';
import styles from './favorite-filters.module.scss';

interface Props {
    scope: FavoriteFilterScope;
}

export default function FavoriteFiltersPage({scope}: Props) {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const items = useAppSelector((s) => s.favoriteFilters.items);
    const propertyFilter = useAppSelector((s) => s.timeTrackingFilters.filter.propertyFilter);
    const detailedByDay = useAppSelector((s) => s.timeTrackingFilters.detailedByDay);
    const visibleDays = useAppSelector((s) => s.timeTrackingFilters.visibleDays);

    const [name, setName] = useState('');
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

    const favorites = useMemo(() => items.filter((f) => f.scope === scope), [items, scope]);
    const backRoute = scope === 'Timesheet' ? NAVIGATION_ROUTES.TIMESHEET : NAVIGATION_ROUTES.TIMEREPORT;
    const pageTitle = intl.formatMessage({id: Lx.General.FAVORITE_FILTERS});

    useEffect(() => {
        dispatch(getFavoriteFilters(scope));
    }, [dispatch, scope]);

    const handleSave = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            return;
        }
        const payload: FavoriteFilterPayload = {propertyFilter, detailedByDay, visibleDays};
        dispatch(createFavoriteFilter({scope, name: trimmed, filterPayload: JSON.stringify(payload)}));
        setName('');
    };

    const handleApply = (favorite: FavoriteFilter) => {
        let parsed: FavoriteFilterPayload;
        try {
            parsed = JSON.parse(favorite.filterPayload) as FavoriteFilterPayload;
        } catch {
            return;
        }
        if (parsed.propertyFilter) {
            updateTimeTrackingPropertyFilter(parsed.propertyFilter);
        }
        updateTimeTrackingDetailedByDay(typeof parsed.detailedByDay === 'boolean' ? parsed.detailedByDay : false);
        updateTimeTrackingVisibleDays(parsed.visibleDays ?? VisibleDays.All);
        navigate(backRoute);
    };

    const handleDelete = (id: number) => {
        dispatch(deleteFavoriteFilter(id));
        setPendingDeleteId(null);
    };

    return (
        <Formik initialValues={{}} onSubmit={async () => undefined}>
            <Form className={styles.formRoot}>
                <BaseForm
                    contentGap={16}
                    formType='create'
                    formHeader={Lx.General.FAVORITE_FILTERS}
                    formHeaderDefaultMessage='Favorite filters'
                    savingTitle={pageTitle}
                    onClose={() => navigate(backRoute)}
                    primaryButtonLabel={intl.formatMessage({id: Lx.General.SAVE})}
                    secondaryButtonLabel={intl.formatMessage({id: Lx.General.CANCEL})}
                    onPrimaryAction={handleSave}
                    disablePrimaryAction={name.trim().length === 0}
                    allowPristineSubmit
                    disableDirtyCheck
                    showSuccessSnack={false}
                    footerClassName={styles.footer}
                >
                    <div className={styles.field}>
                        <label className={styles.fieldLabel} htmlFor='favorite-name'>
                            <FormattedMessage id={Lx.General.FAVORITE_NAME_PLACEHOLDER} />
                        </label>
                        <input
                            id='favorite-name'
                            className={styles.nameInput}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={intl.formatMessage({id: Lx.General.FAVORITE_NAME_PLACEHOLDER})}
                            maxLength={100}
                        />
                    </div>

                    {favorites.length === 0 ? (
                        <div className={styles.empty}><FormattedMessage id={Lx.General.NO_FAVORITE_FILTERS} /></div>
                    ) : (
                        <ul className={styles.list}>
                            {favorites.map((favorite) => (
                                <li key={favorite.id} className={styles.row}>
                                    <span className={styles.name} title={favorite.name}>{favorite.name}</span>
                                    {pendingDeleteId === favorite.id ? (
                                        <div className={styles.actions}>
                                            <span className={styles.confirmText}>
                                                <FormattedMessage id={Lx.General.DELETE_FAVORITE_CONFIRM} values={{name: favorite.name}} />
                                            </span>
                                            <IconButton className={styles.rowIconButton} aria-label='confirm-delete' onClick={() => handleDelete(favorite.id)}>
                                                <SVGS.CheckIcon />
                                            </IconButton>
                                            <IconButton className={styles.rowIconButton} aria-label='cancel-delete' onClick={() => setPendingDeleteId(null)}>
                                                <SVGS.CrossIcon />
                                            </IconButton>
                                        </div>
                                    ) : (
                                        <div className={styles.actions}>
                                            <Button className={styles.applyButton} onClick={() => handleApply(favorite)}>
                                                <FormattedMessage id={Lx.General.APPLY_AND_GO_BACK} />
                                            </Button>
                                            <IconButton className={styles.rowIconButton} aria-label='delete-favorite' onClick={() => setPendingDeleteId(favorite.id)}>
                                                <SVGS.DeleteIcon />
                                            </IconButton>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </BaseForm>
            </Form>
        </Formik>
    );
}
